import React from 'react'

import { Box, Divider, Grid, Stack } from '@mui/material'

import { Skeleton } from '@repo/ui'
import { Header, InfoBlock, InfoItem, InfoRow } from 'components'

/**
 * Loading placeholder for the invoice detail page (§19 — a skeleton fills the SAME space the loaded
 * content will occupy).
 *
 * ## What it replaces
 *
 * `if (isLoading) return <Loader />` — a centred spinner, i.e. no footprint at all. The loaded page
 * is a two-column layout ~489px tall under a 118px header; the spinner reserved none of it, so the
 * whole page materialised at once.
 *
 * ## Measured, not guessed
 *
 * Off `/settings/company/billing/invoices/:id` (viewport 1711, content column x=280 w=1407), on
 * both seeded invoices:
 *
 *   wayfinding row     y=24  h=33
 *   title row          y=69  h=49   — no avatar here, so 49 rather than the owner page's 64
 *   title              195x31 at y=78, status chip 53–60 x 32 beside it
 *   header action      Download PDF 168x49
 *   header total       y=24  h=118
 *   Grid container     y=126 (the `pt: 4` minus MUI's -16px spacing margin)
 *   left column  md=8  x=264 w=949, InfoBlock at x=280 y=158 w=933
 *   right column md=4  x=1213 w=474, InfoBlock at x=1229 y=158 w=458
 *
 *   left InfoBlock     Details title 64 → row 84 → divider 1 → row 84 → divider 1 → row 118
 *                      = 354 total (the 118 row is `valueSpacing={3}` with a 56px avatar preview)
 *   right InfoBlock    title 48 → line items 31 → totals 44, inside p=6 spacing=4 = 203
 *
 * ## Construction, not constants
 *
 * The two plain rows render the REAL `InfoBlock`/`InfoRow`/`InfoItem` with `value={undefined}`,
 * which `InfoItem` already draws as a `<Skeleton width={150} />` — so their 84px is correct by
 * construction and survives a label being added.
 *
 * The third row cannot use that: its values are `UserCell` / `PaymentCell` avatar blocks, and an
 * undefined value would draw a one-line text bar, giving 84px against the real 118. So it passes a
 * value that reproduces the CONTENT of those previews — a 40px avatar plus two text lines inside
 * the same `py: 2` — rather than a hardcoded 118. Same reason the product page's default-tag panel
 * reproduces content: a height literal is only correct at the width and data it was measured at.
 *
 * ## Where it deliberately under-reserves
 *
 * Two regions are data-dependent and are NOT reserved:
 *
 *   - the **shipping-address row** (a fourth InfoRow, +103px). Present on one of the seeded
 *     invoices and absent on the other, and unknowable before the fetch. Reserving it would leave a
 *     103px hole on every invoice without shipping.
 *   - the **"View order" button**, which renders only for an invoice with an `orderId` — neither
 *     seeded invoice has one.
 *
 * The line-item list reserves ONE item (31px). The real list is 31px for a single-line item and was
 * measured at 100px for one that wraps onto more lines; the count and the wrapping are both
 * unknowable up front, so one row is the common denominator.
 */
export const InvoicePageSkeleton = () => {
  /**
   * Stand-in for a `UserCell` / `PaymentCell` value: 56px tall, which is the 40px avatar plus the
   * cells' own `py: 2`. Shaped from the content so the row height follows from it.
   */
  const PreviewSkeleton = () => (
    <Stack direction="row" spacing={3} alignItems="center" sx={{ py: 2 }}>
      <Skeleton variant="circular" width={40} height={40} />
      <Stack spacing={1}>
        <Skeleton width={78} height={14} />
        <Skeleton width={150} height={14} />
      </Stack>
    </Stack>
  )

  return (
    <>
      <Header
        // 195px is the measured width of an invoice number title (`#6HR2ZE8S-0003`), which is a
        // fixed-format id — so unusually, this bar is the right width for every invoice.
        title={<Skeleton width={195} height={31} />}
        breadcrumbs
        // `Header`'s own pill placeholder for the status chip.
        status={null as any}
        // A bar, NOT omitted — `Header` renders the breadcrumb trail only when this is truthy, and
        // omitting it also trips the no-trail branch that pulls the header up 24.5px.
        breadcrumbsOveride={<Skeleton width={150} height={16} />}
        rightComponents={
          <Stack direction="row" spacing={3} alignItems="center">
            {/* "View order" is omitted — see the note on under-reserving above.

                40 is the height `headerActionGroupSx` in Header.tsx enforces on a header button
                (`actionSize` default `medium`); measured 40 on the live page. It is a literal only
                because Header does not export it — a `HEADER_ACTION_HEIGHT` constant is the real
                fix, and until it exists this number has to track that file. It does NOT affect
                footprint: the title row carries `minHeight: 49`, so the header measures 118 either
                way. Getting it wrong is a visual mismatch, not a shift. */}
            <Skeleton variant="rounded" width={168} height={40} sx={{ borderRadius: 2 }} />
          </Stack>
        }
      />

      {/* Same container as the loaded page: `spacing={4}`, `pt: 4`, md 8/4 split. */}
      <Grid container spacing={4} sx={{ pt: 4 }}>
        <Grid item xs={12} md={8}>
          <InfoBlock>
            {/* The "Details" heading block. 24px is the body1 line box the real heading occupies,
                so with the real `p: 6` / `pb: 4` this is the measured 64px. */}
            <Stack p={6} pb={4}>
              <Skeleton width={64} height={24} />
            </Stack>
            <InfoRow>
              <InfoItem label="Invoice ID" value={undefined} />
              <InfoItem label="Date" value={undefined} />
            </InfoRow>
            <Divider sx={{ pr: 4, pl: 4 }} />
            <InfoRow>
              <InfoItem label="Total" value={undefined} />
              <InfoItem label="Type" value={undefined} />
            </InfoRow>
            <Divider sx={{ pr: 4, pl: 4 }} />
            <InfoRow>
              <InfoItem label="Ordered by" value={<PreviewSkeleton />} valueSpacing={3} />
              <InfoItem label="Payment method" value={<PreviewSkeleton />} valueSpacing={3} />
            </InfoRow>
          </InfoBlock>
        </Grid>

        <Grid item xs={12} md={4}>
          <InfoBlock>
            <Stack p={6} spacing={4}>
              {/* "Invoice summary" + its `pb: 6` = the measured 48px. */}
              <Box sx={{ pb: 6 }}>
                <Skeleton width={112} height={24} />
              </Box>
              {/* ONE line item: name + amount row, then the divider the real item draws. */}
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" spacing={3}>
                  <Skeleton width={180} height={18} />
                  <Skeleton width={56} height={18} />
                </Stack>
                <Divider />
              </Stack>
              {/* Subtotal + Total. A Discount row only renders when there IS a discount, so it is
                  not reserved. */}
              <Stack spacing={2}>
                <Stack direction="row" spacing={6} justifyContent="flex-end">
                  <Skeleton width={52} height={18} />
                  <Skeleton width={96} height={18} />
                </Stack>
                <Stack direction="row" spacing={6} justifyContent="flex-end">
                  <Skeleton width={36} height={18} />
                  <Skeleton width={96} height={18} />
                </Stack>
              </Stack>
            </Stack>
          </InfoBlock>
        </Grid>
      </Grid>
    </>
  )
}
